import { useTheme } from 'foundations-theming/utils';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useDebouncedEditorStateContext from 'hubspot-prosemirror/prosemirror/contexts/useDebouncedEditorStateContext';
import useContentSearch from 'hubspot-prosemirror/prosemirror/utils/useContentSearch';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { List } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { buildDisplayText } from './PersonalizationTokenInlinePlugin';
import { StyledTokenV2, personalizationTokenIconMap } from 'hubspot-prosemirror/prosemirror/views/PersonalizationTokenViewV2';
import PersonalizationTokenInlineFallback from './PersonalizationTokenInlineFallback';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function groupTokens(tokens) {
  const groupMap = new Map();
  for (const token of tokens) {
    const group = token.groupName || '';
    const existing = groupMap.get(group);
    if (existing) {
      existing.push(token);
    } else {
      groupMap.set(group, [token]);
    }
  }
  const groups = [];
  for (const [groupName, groupTokensList] of groupMap) {
    groups.push({
      groupName,
      tokens: groupTokensList
    });
  }
  return groups;
}
export default function PersonalizationTokenInlineDropdown({
  pluginKey,
  fetchTokens,
  onSelect,
  onTokensChange,
  onConfirmToken,
  onCancelPendingToken,
  defaultValueConfig,
  renderCustomFields
}) {
  var _pendingToken$tokenSt, _pendingToken$tokenSt2;
  const theme = useTheme();
  const {
    editorState
  } = useDebouncedEditorStateContext();
  const pluginState = pluginKey.getState(editorState);
  const {
    searchText,
    selectedIndex,
    pendingToken
  } = pluginState;
  const [isPendingSearch, setIsPendingSearch] = useState(true);
  const [customDefaultTokenValue, setCustomDefaultTokenValue] = useState('');
  const [customTokenMeta, setCustomTokenMeta] = useState(null);
  const [customInsertDisabled, setCustomInsertDisabled] = useState(false);
  const prevPendingTokenKey = useRef(undefined);
  useEffect(() => {
    var _pendingToken$tokenKe;
    const newKey = (_pendingToken$tokenKe = pendingToken === null || pendingToken === void 0 ? void 0 : pendingToken.tokenKey) !== null && _pendingToken$tokenKe !== void 0 ? _pendingToken$tokenKe : null;
    if (prevPendingTokenKey.current !== undefined && newKey !== prevPendingTokenKey.current) {
      setCustomDefaultTokenValue('');
      setCustomTokenMeta(null);
    }
    prevPendingTokenKey.current = newKey;
  }, [pendingToken]);
  const setTokenAttr = useCallback((key, value) => {
    if (key === 'defaultTokenValue') {
      setCustomDefaultTokenValue(value);
    } else if (key === 'tokenMeta') {
      setCustomTokenMeta(value);
    } else if (key === 'disableInsert') {
      setCustomInsertDisabled(value);
    }
  }, []);
  const trackedFetch = useMemo(() => {
    return args => {
      return fetchTokens(args).finally(() => {
        setIsPendingSearch(false);
      });
    };
  }, [fetchTokens]);
  const [,, setSearchQuery, tokens, isLoading] = useContentSearch(trackedFetch, undefined, undefined, searchText);
  const tokenList = useMemo(() => tokens || [], [tokens]);
  useEffect(() => {
    onTokensChange(tokenList);
  }, [tokenList, onTokensChange]);
  const trimmedSearchText = searchText.trim();
  useEffect(() => {
    setIsPendingSearch(true);
    setSearchQuery(trimmedSearchText);
  }, [trimmedSearchText, setSearchQuery]);
  const selectedRef = useRef(null);
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [selectedIndex, tokenList]);
  const clampedIndex = tokenList.length === 0 ? -1 : Math.min(selectedIndex, tokenList.length - 1);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  useEffect(() => {
    setHoveredIndex(null);
  }, [selectedIndex, tokenList]);
  const activeIndex = hoveredIndex !== null ? hoveredIndex : clampedIndex;
  const handleMouseEnter = useCallback(index => {
    setHoveredIndex(index);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);
  const groups = useMemo(() => groupTokens(tokenList), [tokenList]);
  const selectedStyle = {
    backgroundColor: theme.color['fill-field-hover']
  };
  const dropdownContent = /*#__PURE__*/_jsx(List, {
    styled: false,
    maxBlockSize: "300px",
    minInlineSize: "240px",
    overflowX: "hidden",
    overflowY: "auto",
    position: "relative",
    margin: "0",
    padding: "0",
    "data-test-id": "personalization-token-inline-dropdown-list",
    children: (isLoading || isPendingSearch) && tokenList.length === 0 ? /*#__PURE__*/_jsx("li", {
      children: /*#__PURE__*/_jsx(Flex, {
        minBlockSize: "50px",
        justifyContent: "center",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          size: "sm"
        })
      })
    }) : tokenList.length === 0 ? /*#__PURE__*/_jsx("li", {
      style: {
        padding: `${theme.space['300']}`,
        fontSize: '14px'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.personalizationToken.inlineDropdown.noResults"
      })
    }) : groups.map(group => {
      const groupItems = group.tokens.map(token => {
        const flatIndex = tokenList.findIndex(t => t.tokenKey === token.tokenKey);
        const isActive = flatIndex === activeIndex;
        return /*#__PURE__*/_jsxs("li", {
          style: Object.assign({
            padding: `${theme.space['200']} ${theme.space['300']} ${theme.space['200']} ${theme.space['600']}`,
            cursor: 'pointer',
            fontSize: '14px'
          }, isActive ? selectedStyle : undefined),
          ref: flatIndex === clampedIndex ? selectedRef : undefined,
          onMouseEnter: () => handleMouseEnter(flatIndex),
          onMouseLeave: handleMouseLeave,
          onMouseDown: e => {
            e.preventDefault();
            onSelect(token);
          },
          "data-test-id": "personalization-token-inline-option",
          children: [token.displayText || token.tokenKey, token.description && /*#__PURE__*/_jsx(Text, {
            typography: "detail-200",
            color: "text-core-subtle",
            marginBlockStart: "100",
            "data-test-id": "personalization-token-inline-option-description",
            children: token.description
          })]
        }, token.tokenKey);
      });
      if (!group.groupName) {
        return /*#__PURE__*/_jsx(Fragment, {
          children: groupItems
        }, "ungrouped");
      }
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("li", {
          style: {
            padding: `${theme.space['300']} ${theme.space['400']}`,
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'default'
          },
          "data-test-id": "personalization-token-inline-group-header",
          children: group.groupName
        }), groupItems]
      }, `group-${group.groupName}`);
    })
  });
  const customFieldsContent = useMemo(() => {
    if (!renderCustomFields || !pendingToken) return null;
    return renderCustomFields({
      selectedToken: {
        text: pendingToken.displayText,
        value: pendingToken.tokenKey,
        tokenStyles: pendingToken.tokenStyles
      },
      tokenAttrs: {
        defaultTokenValue: customDefaultTokenValue,
        tokenMeta: customTokenMeta
      },
      setTokenAttr,
      isEditing: false
    });
  }, [renderCustomFields, pendingToken, customDefaultTokenValue, customTokenMeta, setTokenAttr]);
  const handleCustomFieldsInsert = useCallback(() => {
    onConfirmToken(customDefaultTokenValue || undefined, customTokenMeta);
  }, [onConfirmToken, customDefaultTokenValue, customTokenMeta]);
  const handleCustomFieldsKeyDown = useCallback(e => {
    if (e.key === 'Enter' && !customInsertDisabled) {
      e.preventDefault();
      e.stopPropagation();
      handleCustomFieldsInsert();
    }
  }, [customInsertDisabled, handleCustomFieldsInsert]);
  const popoverContent = pendingToken ? customFieldsContent ? /*#__PURE__*/_jsx("div", {
    onKeyDown: handleCustomFieldsKeyDown,
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "200",
      padding: "400",
      minInlineSize: "300px",
      onMouseDown: e => {
        if (e.target.tagName !== 'INPUT') {
          e.preventDefault();
        }
      },
      "data-test-id": "personalization-token-inline-custom-fields",
      children: [/*#__PURE__*/_jsxs(StyledTokenV2, {
        tokenState: "idle",
        $isP2: false,
        style: {
          alignSelf: 'flex-start'
        },
        "data-test-id": "personalization-token-inline-custom-fields-chip",
        children: [personalizationTokenIconMap[(_pendingToken$tokenSt = pendingToken.tokenStyles) === null || _pendingToken$tokenSt === void 0 ? void 0 : _pendingToken$tokenSt.tokenIconKey] && /*#__PURE__*/_jsx(IconHolder, {
          icon: personalizationTokenIconMap[(_pendingToken$tokenSt2 = pendingToken.tokenStyles) === null || _pendingToken$tokenSt2 === void 0 ? void 0 : _pendingToken$tokenSt2.tokenIconKey],
          padding: "0"
        }), buildDisplayText(pendingToken)]
      }), customFieldsContent, /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        flexDirection: "row",
        marginBlockStart: "100",
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          size: "sm",
          onClick: handleCustomFieldsInsert,
          disabled: customInsertDisabled || undefined,
          "data-test-id": "personalization-token-inline-custom-fields-insert",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "hubspot-prosemirror.personalizationToken.popover.insertButton"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "sm",
          onClick: onCancelPendingToken,
          "data-test-id": "personalization-token-inline-custom-fields-cancel",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "hubspot-prosemirror.personalizationToken.inlineDropdown.cancelButton"
          })
        })]
      })]
    })
  }) : /*#__PURE__*/_jsx(PersonalizationTokenInlineFallback, {
    token: pendingToken,
    isRequired: defaultValueConfig === null || defaultValueConfig === void 0 ? void 0 : defaultValueConfig.required,
    onInsert: onConfirmToken,
    onCancel: onCancelPendingToken
  }) : dropdownContent;
  return /*#__PURE__*/_jsx(Popover, {
    autoPlacement: true,
    arrowSize: "none",
    content: popoverContent,
    open: true,
    placement: "bottom right",
    "data-test-id": "personalization-token-inline-popover",
    children: /*#__PURE__*/_jsx("span", {})
  });
}